// Vendor-side licence administration: list / create / revoke licence keys and
// inspect (or kick) the devices activated against them. Powers the Licenses
// page (/licenses). The customer-facing activation/validation logic lives in
// services/license.ts.
//
// IMPORTANT — extra Supabase setup is required for this page's WRITE actions.
// The base setup (see license.ts) only lets clients READ the `licenses` table.
// Creating, editing, or revoking keys from inside the app needs write policies:
//
//   create policy "licenses write" on public.licenses
//     for all to anon using (true) with check (true);
//
// SECURITY WARNING: that policy lets anyone holding the (public) anon key mint
// or revoke licences, which undermines the whole system. Only add it if this
// build is your private admin tool. The proper fix is to manage licences with
// the service-role key or behind Supabase Auth, not the publishable key shipped
// to customers.

import { REST, sbHeaders, sbError } from './supabase';

export interface LicenseAdminRow {
  key: string;
  plan: string;
  status: string;
  expires_at: string | null;
  max_devices: number;
  customer_name: string | null;
  created_at: string;
}

export interface ActivationAdminRow {
  id: string;
  license_key: string;
  device_id: string;
  device_name: string | null;
  last_seen: string;
  created_at: string;
}

export interface NewLicenseInput {
  key: string;
  plan: string;
  maxDevices: number;
  customerName: string;
  validityDays: number | null; // null = lifetime (no expiry)
}

// EPOS-XXXX-XXXX-XXXX style key, omitting easily-confused characters.
export function generateKey(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const block = () =>
    Array.from({ length: 4 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
  return `EPOS-${block()}-${block()}-${block()}`;
}

async function notSetUp(res: Response): Promise<Error> {
  if (res.status === 404) {
    return new Error('Licence tables not set up in Supabase yet (see setup SQL in license.ts).');
  }
  return sbError(res);
}

export async function listLicenses(): Promise<LicenseAdminRow[]> {
  const url = `${REST}/licenses?select=*&order=created_at.desc`;
  const res = await fetch(url, { headers: sbHeaders() });
  if (!res.ok) throw await notSetUp(res);
  return (await res.json()) as LicenseAdminRow[];
}

export async function createLicense(input: NewLicenseInput): Promise<LicenseAdminRow> {
  const key = input.key.trim().toUpperCase();
  if (!key) throw new Error('Licence key is required.');
  const expires_at =
    input.validityDays == null
      ? null
      : new Date(Date.now() + input.validityDays * 24 * 60 * 60 * 1000).toISOString();
  const res = await fetch(`${REST}/licenses`, {
    method: 'POST',
    headers: sbHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify({
      key,
      plan: input.plan,
      status: 'active',
      max_devices: input.maxDevices,
      customer_name: input.customerName.trim() || null,
      expires_at,
    }),
  });
  if (!res.ok) {
    if (res.status === 409) throw new Error('That licence key already exists.');
    throw await notSetUp(res);
  }
  const rows = (await res.json()) as LicenseAdminRow[];
  return rows[0];
}

export async function setLicenseStatus(key: string, status: string): Promise<void> {
  const res = await fetch(`${REST}/licenses?key=eq.${encodeURIComponent(key)}`, {
    method: 'PATCH',
    headers: sbHeaders({ Prefer: 'return=minimal' }),
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw await notSetUp(res);
}

export async function deleteLicense(key: string): Promise<void> {
  const res = await fetch(`${REST}/licenses?key=eq.${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: sbHeaders({ Prefer: 'return=minimal' }),
  });
  if (!res.ok) throw await notSetUp(res);
}

export async function listActivations(key: string): Promise<ActivationAdminRow[]> {
  const url =
    `${REST}/activations?select=*` +
    `&license_key=eq.${encodeURIComponent(key)}&order=last_seen.desc`;
  const res = await fetch(url, { headers: sbHeaders() });
  if (!res.ok) throw await notSetUp(res);
  return (await res.json()) as ActivationAdminRow[];
}

// Kick a device: removing its activation frees a slot and (on that device's
// next online check) locks it until the key is re-entered.
export async function removeActivation(id: string): Promise<void> {
  const res = await fetch(`${REST}/activations?id=eq.${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: sbHeaders({ Prefer: 'return=minimal' }),
  });
  if (!res.ok) throw await notSetUp(res);
}
